import { toDateInput } from './datetime';

export const EVENT_ESTIMATED_DURATION_PRESETS = [
  '未定',
  '約60分',
  '約90分',
  '約2時間',
  '2時間〜2時間30分',
  '約3時間',
  '3時間以上',
];

export const EVENT_PERFORMANCE_FORMAT_PRESETS = [
  '未定',
  '通し公演（休憩なし）',
  '通し公演（休憩あり）',
  '2部制（休憩あり）',
  '2部制（休憩なし）',
  '3部以上',
];

export const getEventEstimatedDurationText = (event) =>
  String(event?.estimated_duration_text || event?.estimatedDurationText || '').trim();

export const getEventPerformanceFormatText = (event) =>
  String(event?.performance_format_text || event?.performanceFormatText || '').trim();

export const getEventPerformanceStructureRows = (event, { includeEmpty = false } = {}) => {
  const rows = [
    { key: 'estimated_duration_text', label: '所要時間', value: getEventEstimatedDurationText(event) },
    { key: 'performance_format_text', label: '進行形式', value: getEventPerformanceFormatText(event) },
  ];
  return includeEmpty
    ? rows.map((row) => ({ ...row, value: row.value || '未設定' }))
    : rows.filter((row) => row.value);
};

export const getEventPerformances = (event) =>
  Array.isArray(event?.performances) ? event.performances : [];

export const isMultiPerformanceEvent = (event) =>
  String(event?.event_mode || event?.eventMode || '').toLowerCase() === 'multi_performance' ||
  getEventPerformances(event).length > 0;

export const getPerformanceName = (performance, fallback = '') =>
  performance?.performance_name ||
  performance?.performanceName ||
  performance?.name ||
  fallback ||
  '';

export const getPerformanceDate = (performance) =>
  toDateInput(performance?.performance_date || performance?.performanceDate || performance?.event_date || '');

export const formatPerformanceSummary = (event, max = 3) => {
  const performances = getEventPerformances(event);
  if (!performances.length) return '';
  const labels = performances.slice(0, max).map((performance, index) => {
    const date = getPerformanceDate(performance);
    const name = getPerformanceName(performance, `公演${index + 1}`);
    return [date ? date.replace(/-/g, '/') : '', name].filter(Boolean).join(' ');
  });
  const suffix = performances.length > max ? ` / 他${performances.length - max}公演` : '';
  return `${labels.join(' / ')}${suffix}`;
};

export const formatPerformanceDateRangeShort = (event) => {
  const dates = getEventPerformances(event)
    .map((performance) => getPerformanceDate(performance))
    .filter(Boolean)
    .sort();
  if (!dates.length) return '';
  const toMonthDay = (ymd) => {
    const [, month, day] = String(ymd).split('-').map(Number);
    if (!month || !day) return '';
    return `${month}/${day}`;
  };
  const first = toMonthDay(dates[0]);
  const last = toMonthDay(dates[dates.length - 1]);
  if (!first) return '';
  return first === last || !last ? first : `${first}〜${last}`;
};

export const getEventFormLabel = (event) => {
  const name = event?.name || event?.event_name || event?.eventName || '';
  if (isMultiPerformanceEvent(event)) {
    return name || 'イベント';
  }
  const performance = event?.performance_type || event?.performanceType || '';
  return [name, performance].filter(Boolean).join(' ').trim() || 'イベント';
};

export const normalizeEventCandidatesForForms = (events = []) => {
  const map = new Map();
  (Array.isArray(events) ? events : []).forEach((event) => {
    const eventUuid = event?.event_uuid || event?.eventUuid || event?.uuid;
    if (!eventUuid) return;
    const key = String(eventUuid);
    const current = map.get(key);
    if (!current) {
      map.set(key, {
        ...event,
        performance_type: isMultiPerformanceEvent(event) ? '' : event?.performance_type || '',
        performances: getEventPerformances(event),
      });
      return;
    }
    const performanceById = new Map(
      getEventPerformances(current).map((performance, index) => [
        String(performance?.id || performance?.performance_id || performance?.sort_order || index),
        performance,
      ])
    );
    getEventPerformances(event).forEach((performance, index) => {
      const performanceKey = String(performance?.id || performance?.performance_id || performance?.sort_order || index);
      if (!performanceById.has(performanceKey)) performanceById.set(performanceKey, performance);
    });
    map.set(key, {
      ...current,
      event_mode: current.event_mode || event.event_mode,
      performances: Array.from(performanceById.values()),
    });
  });
  return Array.from(map.values());
};
